import { useEffect, useRef, useState } from 'react'
import { captureKey, keyLabel } from '@/lib/audio/ptt'
import { desktop } from '@/lib/desktop'
import { HOTKEY_ACTIONS, comboFromEvent, comboFromMouse, defaultCombo, formatCombo, formatHeld, isMouseCode, sameCombo, type Combo, type HotkeyAction } from '@/lib/hotkeys'
import { consumePress } from '@/lib/mousePress'
import { cx } from '@/lib/cx'
import { useAppStore } from '@/store/app'
import { isMac, useHotkeyStore } from '@/store/hotkeys'
import { DEFAULT_PTT_KEY, useVoiceStore } from '@/store/voice'
import layer from './SettingsLayer.module.css'
import styles from './KeybindsSection.module.css'

type Background = 'ok' | 'permission' | 'unavailable' | 'old' | 'browser' | null

const labelOf = (id: HotkeyAction) => HOTKEY_ACTIONS.find((a) => a.id === id)?.label ?? id

/** «Горячие клавиши»: сочетания для микрофона, звука и быстрого перехода; клавиша рации — та же, что в «Голосе и видео». */
export function KeybindsSection() {
  const [background, setBackground] = useState<Background>(() => (!desktop ? 'browser' : !desktop.hotkeys ? 'old' : null))
  const [note, setNote] = useState<string | null>(null)
  // оболочка до 0.1.21 включительно ловит в фоне только клавиатуру
  const mouseNotInBackground = useHotkeyStore((s) => !desktop?.hotkeys?.mouse && HOTKEY_ACTIONS.some((a) => a.global && isMouseCode(s.bindings[a.id]?.code ?? '')))

  // разрешение macOS выдают в системных настройках — перепроверяем, когда окно снова в фокусе
  useEffect(() => {
    const bridge = desktop?.hotkeys
    if (!bridge) return
    const check = () => void bridge.status().then(setBackground).catch(() => setBackground('unavailable'))
    check()
    window.addEventListener('focus', check)
    return () => window.removeEventListener('focus', check)
  }, [])

  return (
    <section>
      <h1 className={layer.sectionTitle}>Горячие клавиши</h1>
      <p className={layer.text}>{backgroundText(background)}</p>
      {background === 'ok' && mouseNotInBackground ? <p className={layer.text}>Кнопки мыши в фоне — после обновления приложения.</p> : null}
      {background === 'permission' ? (
        <button type="button" className={layer.button} onClick={() => desktop?.openPrivacySettings('accessibility')}>Открыть «Универсальный доступ»</button>
      ) : null}

      <h2 className={layer.subTitle}>Голос</h2>
      <div className={styles.list}>
        <ComboRow action="toggleMute" onNote={setNote} />
        <ComboRow action="toggleDeafen" onNote={setNote} />
        {desktop?.overlay ? <ComboRow action="toggleOverlay" onNote={setNote} /> : null}
        <PttRow />
      </div>

      <h2 className={layer.subTitle}>Навигация</h2>
      <div className={styles.list}>
        <ComboRow action="quickSwitcher" onNote={setNote} />
      </div>
      <p className={layer.text}>Быстрый переход открывается и из поля сообщения.</p>
      {note ? <p className={styles.note} role="status">{note}</p> : null}
    </section>
  )
}

function backgroundText(b: Background): string {
  switch (b) {
    case 'browser':
      return 'Сочетания работают, пока вкладка Кличата активна. В фоне их ловит приложение для компьютера.'
    case 'old':
      return 'Работают, когда окно Кличата активно. В фоне — после обновления приложения'
    case 'ok':
      return 'Микрофон, звук и рация работают и поверх игр, пока вы в голосовом канале.'
    case 'permission':
      return 'В фоне сочетания не работают: разрешите Кличату «Универсальный доступ» в настройках macOS.'
    case 'unavailable':
      return 'Перехват клавиш недоступен на этой системе: сочетания работают, только когда окно Кличата активно.'
    default:
      return ''
  }
}

/** Строка сочетания: текущее, «Назначить» / «Изменить» и «Сбросить», если отличается от умолчания. */
function ComboRow({ action, onNote }: { action: HotkeyAction; onNote: (text: string | null) => void }) {
  const combo = useHotkeyStore((s) => s.bindings[action])
  const assign = useHotkeyStore((s) => s.assign)
  const reset = useHotkeyStore((s) => s.reset)
  const setRecording = useHotkeyStore((s) => s.setRecording)
  const [recording, setLocalRecording] = useState(false)
  const [held, setHeld] = useState('')
  const rowRef = useRef<HTMLDivElement | null>(null)
  const isDefault = sameCombo(combo, defaultCombo(action, isMac))

  useEffect(() => {
    if (!recording) return
    setRecording(true)
    const stop = () => {
      setLocalRecording(false)
      setHeld('')
    }
    // ловим раньше всех (window, фаза захвата): иначе Esc закрыл бы настройки, а Ctrl+K открыл бы переход
    const onDown = (e: KeyboardEvent) => {
      e.preventDefault()
      e.stopPropagation()
      const bare = !e.ctrlKey && !e.altKey && !e.shiftKey && !e.metaKey
      if (bare && e.code === 'Escape') {
        stop()
        return
      }
      if (bare && (e.code === 'Backspace' || e.code === 'Delete')) {
        assign(action, null)
        onNote(null)
        stop()
        return
      }
      const next = comboFromEvent(e)
      if (!next) {
        setHeld(formatHeld(e, isMac))
        return
      }
      save(next)
    }
    const save = (next: Combo) => {
      const other = assign(action, next)
      onNote(other ? `${formatCombo(next, isMac)} было у действия «${labelOf(other)}» — там сочетание снято.` : null)
      stop()
    }
    const onUp = (e: KeyboardEvent) => {
      e.preventDefault()
      e.stopPropagation()
      setHeld(formatHeld(e, isMac))
    }
    // кнопка мыши тоже сочетание; обычный клик левой или правой проходит дальше — им жмут «Отмена»
    const onButton = (e: MouseEvent) => {
      const next = comboFromMouse(e)
      if (!next) return
      consumePress(e)
      e.stopPropagation()
      save(next)
    }
    // клик мимо строки — передумали назначать
    const onMouse = (e: MouseEvent) => {
      if (!rowRef.current?.contains(e.target as Node)) stop()
    }
    window.addEventListener('keydown', onDown, true)
    window.addEventListener('keyup', onUp, true)
    window.addEventListener('mousedown', onButton, true)
    window.addEventListener('blur', stop)
    document.addEventListener('mousedown', onMouse)
    return () => {
      window.removeEventListener('keydown', onDown, true)
      window.removeEventListener('keyup', onUp, true)
      window.removeEventListener('mousedown', onButton, true)
      window.removeEventListener('blur', stop)
      document.removeEventListener('mousedown', onMouse)
      setRecording(false)
    }
  }, [recording, action, assign, onNote, setRecording])

  const label = labelOf(action)
  return (
    <div className={styles.row} ref={rowRef}>
      <span className={styles.label}>{label}</span>
      <span className={styles.value}>
        {recording ? (
          <kbd className={cx(styles.kbd, styles.kbdRecording)}>{held ? `${held}…` : 'Нажмите сочетание'}</kbd>
        ) : combo ? (
          <kbd className={styles.kbd}>{formatCombo(combo, isMac)}</kbd>
        ) : (
          <span className={styles.none}>Не назначено</span>
        )}
      </span>
      <span className={styles.actions}>
        <button type="button" className={cx(layer.button, layer.buttonSecondary)} aria-label={recording ? `Отменить запись: ${label}` : `${combo ? 'Изменить' : 'Назначить'}: ${label}`} onClick={() => setLocalRecording((v) => !v)}>
          {recording ? 'Отмена' : combo ? 'Изменить' : 'Назначить'}
        </button>
        {!isDefault && !recording ? (
          <button type="button" className={styles.reset} aria-label={`Сбросить: ${label}`} onClick={() => { reset(action); onNote(null) }}>
            Сбросить
          </button>
        ) : null}
      </span>
      {recording ? <span className={styles.hint}>Подойдёт и кнопка мыши. Esc отменяет запись, Backspace снимает сочетание</span> : null}
    </div>
  )
}

/** Клавиша рации: та же настройка, что в «Голосе и видео», — здесь её видно рядом с остальными. */
function PttRow() {
  const pttKey = useVoiceStore((s) => s.prefs.pttKey)
  const mode = useVoiceStore((s) => s.prefs.inputMode)
  const setPrefs = useVoiceStore((s) => s.setPrefs)
  const setRecording = useHotkeyStore((s) => s.setRecording)
  const openSettings = useAppStore((s) => s.openSettings)
  const [capturing, setCapturing] = useState(false)

  const change = async () => {
    setCapturing(true)
    setRecording(true)
    try {
      const code = await captureKey()
      if (code) await setPrefs({ pttKey: code })
    } finally {
      setRecording(false)
      setCapturing(false)
    }
  }

  return (
    <div className={styles.row}>
      <span className={styles.label}>
        Рация (push-to-talk)
        <span className={styles.sub}>
          {mode === 'ptt' ? (isMouseCode(pttKey) ? 'Голос передаётся, пока кнопка нажата.' : 'Голос передаётся, пока клавиша нажата.') : 'Сейчас микрофон включается голосом.'}{' '}
          <button type="button" className={styles.link} onClick={() => openSettings('voice')}>Режим — в «Голосе и видео»</button>
        </span>
      </span>
      <span className={styles.value}>
        {capturing ? <kbd className={cx(styles.kbd, styles.kbdRecording)}>Нажмите клавишу</kbd> : <kbd className={styles.kbd}>{keyLabel(pttKey)}</kbd>}
      </span>
      <span className={styles.actions}>
        <button type="button" className={cx(layer.button, layer.buttonSecondary)} disabled={capturing} aria-label="Изменить клавишу рации" onClick={() => void change()}>
          {capturing ? 'Ждём клавишу…' : 'Изменить'}
        </button>
        {pttKey !== DEFAULT_PTT_KEY && !capturing ? (
          <button type="button" className={styles.reset} aria-label="Сбросить клавишу рации" onClick={() => void setPrefs({ pttKey: DEFAULT_PTT_KEY })}>
            Сбросить
          </button>
        ) : null}
      </span>
      {capturing ? <span className={styles.hint}>Клавиша, средняя или боковая кнопка мыши. Esc — отмена</span> : null}
    </div>
  )
}
